import React from 'react';
import { CheckCircle, XCircle, AlertTriangle, Clock, RefreshCw } from 'lucide-react';

interface HealthCardProps {
  title: string;
  status: 'healthy' | 'unhealthy' | 'warning' | 'loading' | 'unknown';
  message?: string;
  details?: Array<{
    label: string;
    value: string;
    status?: 'healthy' | 'unhealthy' | 'warning' | 'unknown';
  }>;
  lastUpdated?: string;
  onRefresh?: () => void;
  isRefreshing?: boolean;
}

const HealthCard: React.FC<HealthCardProps> = ({
  title,
  status,
  message,
  details,
  lastUpdated,
  onRefresh,
  isRefreshing = false
}) => {
  const getStatusIcon = () => {
    switch (status) {
      case 'healthy':
        return <CheckCircle className="w-5 h-5 text-green-400" />;
      case 'unhealthy':
        return <XCircle className="w-5 h-5 text-red-400" />;
      case 'warning':
        return <AlertTriangle className="w-5 h-5 text-yellow-400" />;
      case 'loading':
        return <RefreshCw className="w-5 h-5 text-blue-400 animate-spin" />;
      default:
        return <Clock className="w-5 h-5 text-gray-400" />;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'healthy':
        return 'border-green-600 bg-green-900/20';
      case 'unhealthy':
        return 'border-red-600 bg-red-900/20';
      case 'warning':
        return 'border-yellow-600 bg-yellow-900/20';
      case 'loading':
        return 'border-blue-600 bg-blue-900/20';
      default:
        return 'border-gray-600 bg-gray-800';
    }
  };

  const getDetailStatusColor = (detailStatus?: string) => {
    switch (detailStatus) {
      case 'healthy':
        return 'text-green-400';
      case 'unhealthy':
        return 'text-red-400';
      case 'warning':
        return 'text-yellow-400';
      default:
        return 'text-gray-300';
    }
  };

  return (
    <div className={`rounded-lg border p-6 ${getStatusColor()}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          {getStatusIcon()}
          <h3 className="text-lg font-semibold text-white">{title}</h3>
        </div>
        {onRefresh && (
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="p-2 text-gray-400 hover:text-gray-200 hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50"
            title="Refresh"
          >
            <RefreshCw className={`w-4 h-4 ${isRefreshing ? 'animate-spin' : ''}`} />
          </button>
        )}
      </div>

      {message && (
        <p className="text-gray-300 mb-4">{message}</p>
      )}

      {details && details.length > 0 && (
        <div className="space-y-2 mb-4">
          {details.map((detail, index) => (
            <div key={index} className="flex justify-between items-center">
              <span className="text-gray-400">{detail.label}</span>
              <span className={getDetailStatusColor(detail.status)}>
                {detail.value}
              </span>
            </div>
          ))}
        </div>
      )}

      {lastUpdated && (
        <div className="text-sm text-gray-500 pt-2 border-t border-gray-700">
          Last updated: {new Date(lastUpdated).toLocaleString()}
        </div>
      )}
    </div>
  );
};

export default HealthCard;
